import { useState, type ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface AccordionProps {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
}

export function Accordion({
  title,
  icon,
  children,
  defaultOpen = false,
  className,
  headerClassName,
  contentClassName,
}: AccordionProps) {
  const [open, setOpen] = useState(defaultOpen);

  const toggle = () => setOpen((prev) => !prev);

  return (
    <Card className={cn("border-2 shadow-lg overflow-hidden", className)}>
      <CardHeader
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggle();
          }
        }}
        className={cn(
          "border-b cursor-pointer select-none bg-card",
          headerClassName ?? "bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20",
        )}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            {icon}
            <CardTitle className="text-2xl">{title}</CardTitle>
          </div>
          <ChevronDown
            className={cn(
              "h-5 w-5 text-muted-foreground transition-transform duration-200",
              open ? "rotate-180" : "",
            )}
          />
        </div>
      </CardHeader>
      {open && (
        <CardContent className={cn("pt-6", contentClassName)}>
          {children}
        </CardContent>
      )}
    </Card>
  );
}

